import React, { useRef, useMemo, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

interface IPInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  id: string;
  label?: string;
  className?: string;
  wrapperClassName?: string;
  error?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  disabledLength?: number;
  dataTestId: string;
}

const IPInput: React.FC<IPInputProps> = ({
  id,
  label,
  className,
  wrapperClassName,
  error,
  value,
  onChange,
  placeholder = '192.168.1.0',
  disabled = false,
  disabledLength = -1,
  dataTestId,
}) => {
  const placeholders = placeholder.split('.');
  const [values, setValues] = useState(['', '', '', '']);
  const inputsRef = useRef<HTMLInputElement[]>([]);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (disabledLength > 0) {
      const parts = value.split('.');
      if (parts.length <= 4) {
        setValues([...parts, ...Array(4 - parts.length).fill('')]);
      } else {
        setValues(parts);
      }
    } else {
      const parts = value.split('.');
      if (parts.length === 4 && parts.every((part) => /^\d{1,3}$/.test(part))) {
        setValues(parts);
      }
    }
  }, [value, disabledLength]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    setIsDirty(true);
    const inputValue = e.target.value;
    if (/^\d*$/.test(inputValue) && inputValue.length <= 3) {
      const newValues = [...values];
      newValues[index] = inputValue;
      setValues(newValues);

      onChange(newValues.join('.'));

      if (inputValue.length === 3 && index < 3) {
        inputsRef.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.key === 'Backspace' && values[index] === '' && index > 0) {
      inputsRef.current[index - 1].focus();
    } else if (e.key === '.' && index < 3) {
      e.preventDefault();
      inputsRef.current[index + 1].focus();
    }
  };

  const handlePaste = (
    e: React.ClipboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    const ipParts = pastedData.split('.');

    const isFullIp =
      ipParts.length === 4 && ipParts.every((part) => /^\d{1,3}$/.test(part));
    const isSingleOctet =
      /^\d{1,3}$/.test(pastedData) && pastedData.length <= 3;

    if (isFullIp) {
      const newValues = [...values];
      for (let i = 0; i < 4; i += 1) {
        if (i >= disabledLength) {
          newValues[i] = ipParts[i] ?? '';
        }
      }
      setValues(newValues);
      onChange(newValues.join('.'));
    } else if (isSingleOctet && index >= disabledLength) {
      const newValues = [...values];
      newValues[index] = pastedData;
      setValues(newValues);
      onChange(newValues.join('.'));
    } else if (!isFullIp && !isSingleOctet) {
      toast.error('Paste invalid IP address');
    }
  };

  const handleCopy = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const ipString = values.join('.');
    e.clipboardData.setData('text', ipString);
    toast.success('Copied IP address');
  };

  const showError = useMemo(() => isDirty || value, [isDirty, value]);

  return (
    <div
      className={twMerge([
        'flex w-full flex-col items-start justify-start gap-1',
        wrapperClassName,
      ])}
      data-testid={dataTestId}
    >
      {label && (
        <label
          htmlFor={id}
          className="block text-left text-sm font-medium leading-6 text-gray-900"
        >
          {label}
        </label>
      )}
      <div
        className={twMerge([
          'flex items-center overflow-hidden rounded-md border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-blue-500',
          error && 'border-red-500',
          disabled && 'bg-gray-200',
          className,
        ])}
      >
        {values.map((valuePart, index) => {
          const isDisabled = disabled || disabledLength > index;
          const showPlaceholder = !isDisabled && disabledLength < index;
          return (
            <React.Fragment key={index}>
              <input
                type="text"
                placeholder={showPlaceholder ? placeholders[index] : ''}
                value={valuePart}
                onChange={(e) => handleChange(e, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onPaste={(e) => handlePaste(e, index)}
                onCopy={handleCopy}
                ref={(el) => {
                  inputsRef.current[index] = el!;
                }}
                className={twMerge([
                  'h-[34px] w-10 text-center focus:outline-none disabled:bg-gray-200',
                ])}
                maxLength={3}
                disabled={isDisabled}
                data-testid={`${dataTestId}-input-${index}`}
              />
              {index < 3 && (
                <span
                  className={twMerge([
                    'h-[34px] px-0.5 pt-[6px] text-base',
                    disabledLength > index && 'bg-gray-200',
                  ])}
                >
                  .
                </span>
              )}
            </React.Fragment>
          );
        })}
      </div>
      {showError && error && (
        <p className="text-left text-sm text-red-500" id={`${id}-error`}>
          {error}
        </p>
      )}
    </div>
  );
};

export default IPInput;
